import { ConstraintType } from "types/ConstraintsTypes";
import { isEmptyObject, isEqual } from "./misc";
import { StoreApi } from "zustand";

interface ConstraintState {
  messageId: string;
  data: ConstraintType["data"];
}

export function injectDefaultConstraints(
  constraintsStateStandard,
  constraintsStateDefault,
  constraintsStateDefaultMacro,
  financialPlanStore,
) {
  let constraintsState = null;
  constraintsState = {
    ...constraintsStateStandard,
    data: {
      ...constraintsStateStandard.data,
      risk: parseRiskFromDefault(constraintsStateDefault.risk),
      indicator: parseDefaultConstraints(constraintsStateDefault.indicator),
      dividend: parseDefaultMaxMinConstraints(constraintsStateDefault.dividend),
      cost: parseDefaultMaxMinConstraints(constraintsStateDefault.cost),
      asset: parseDefaultMaxMinConstraints(constraintsStateDefault.asset),
      top_quality: parseDefaultConstraintsTopQuality(
        constraintsStateDefault.top_quality,
      ),
      macro_asset_allocation: parseDefaultMaxMinConstraints(
        constraintsStateDefault.macro_asset_allocation,
      ),
      macro: parseDefaultConstraintsMacro(
        constraintsStateDefaultMacro[financialPlanStore],
      ),
      currency_exposure: parseDefaultMaxMinConstraints(
        constraintsStateDefault.currency_exposure,
      ),
      product: constraintsStateStandard.data.product,
      microcategoria_advice:
        constraintsStateStandard.data.microcategoria_advice,
    },
  };
  return constraintsState;
}
const parseRiskFromDefault = (riskDataDefault: any): any => {
  const returnRiskData = {
    financial_plan: riskDataDefault.financial_plan.default,
    risk_profile: riskDataDefault.risk_profile.default,
  };

  return returnRiskData;
};
const parseDefaultConstraintsMacro = (defaultConstraints: any): any => {
  const returnDefaultObject = {};
  for (const key in defaultConstraints) {
    returnDefaultObject[key] = defaultConstraints[key];
  }
  return returnDefaultObject;
};
const parseDefaultConstraintsTopQuality = (
  defaultConstraintsData: any,
): any => {
  const returnDefaultObject = {};
  for (const key in defaultConstraintsData) {
    returnDefaultObject[key] = defaultConstraintsData[key].default;
  }
  //@ts-ignore
  delete returnDefaultObject.type;
  //@ts-ignore
  delete returnDefaultObject.label;
  return returnDefaultObject;
};
const parseDefaultMaxMinConstraints = (defaultConstraintsData: any): any => {
  const returnDefaultObject = {};
  for (let key in defaultConstraintsData) {
    const constraintValueObj = defaultConstraintsData[key];
    if (constraintValueObj[key + "_min"]) {
      returnDefaultObject[key + "_min"] =
        constraintValueObj[key + "_min"].default;
    }
    if (constraintValueObj[key + "_max"]) {
      returnDefaultObject[key + "_max"] =
        constraintValueObj[key + "_max"].default;
    }
  }

  return returnDefaultObject;
};
const parseDefaultConstraints = (defaultConstraints: any): any => {
  const returnDefaultObject = {};
  for (const key in defaultConstraints) {
    returnDefaultObject[key] = defaultConstraints[key].default;
  }
  return returnDefaultObject;
};

export const detectConstraintChanges = (
  msgId: string,
  tempSavedConstraintsState: any,
  exception = null,
  //
  get, //get().isPortfolioCreated
  set, //get().isPortfolioCreated
  constraints, //constraints
) => {
  const tempKeysChangedArr = [];

  const { hasCreated, messageId } = get().isPortfolioCreated;

  let isNewPortfolio = true;
  const portfolioMsgIds = get().portfolioMsgIds;
  for (let i = 0; i < portfolioMsgIds.length; i++) {
    if (messageId === portfolioMsgIds[i]) {
      isNewPortfolio = false;
    }
  }
  if (hasCreated && messageId !== msgId && isNewPortfolio) {
    get().resetConstraintChangesUpdate();
    get().setIsPortfolioCreated({ messageId: "", hasCreated: false });

    //** let's update the blocked list */
    const tempPortfolioMsgIds = [...get().portfolioMsgIds];
    tempPortfolioMsgIds.push(messageId);
    set({
      portfolioMsgIds: tempPortfolioMsgIds,
    });
  }

  //** merge instrument_type fields into asset because BE sends data in two different format */
  constraints.data["asset"] = {
    ...constraints.data["asset"],
    etf_max: constraints.data["instrument_type"].etf_max,
    etf_min: constraints.data["instrument_type"].etf_min,
    mutual_funds_max: constraints.data["instrument_type"].mutual_funds_max,
    mutual_funds_min: constraints.data["instrument_type"].mutual_funds_min,
    bonds_max: constraints.data["instrument_type"].bonds_max,
    bonds_min: constraints.data["instrument_type"].bonds_min,
    core_max: constraints.data["core_satellite"].core_max,
    core_min: constraints.data["core_satellite"].core_min,
    satellite_max: constraints.data["core_satellite"].satellite_max,
    satellite_min: constraints.data["core_satellite"].satellite_min,
  };

  for (const key in constraints.data) {
    if (key === "risk") {
      constraints.data[key] = cleanRisk(constraints.data[key]);
    }
    if (!isEmptyObject(tempSavedConstraintsState)) {
      if (
        !isEqual(constraints.data[key], tempSavedConstraintsState.data[key])
      ) {
        const diffKeys = getConstraintDiff(
          constraints.data[key],
          tempSavedConstraintsState.data[key],
        );

        tempKeysChangedArr.push({
          key: key,
          values: diffKeys,
          seen: false,
        });
      }

      if (exception) {
        if (key === "product" && exception.productException) {
          tempKeysChangedArr.push({
            key: key,
            values: [],
            seen: false,
          });
        }
        if (key === "microcategoria_advice" && exception.microCatException) {
          tempKeysChangedArr.push({
            key: key,
            values: [],
            seen: false,
          });
        }
      }
    }
  }

  const mergeDuplicateKeys = (updates) => {
    const mergedMap = new Map();

    updates.forEach((item) => {
      if (!mergedMap.has(item.key)) {
        mergedMap.set(item.key, {
          key: item.key,
          values: [...item.values],
          seen: item.seen,
        });
      } else {
        const existing = mergedMap.get(item.key);
        existing.values.push(...item.values);
        existing.seen = item.seen;
      }
    });

    const mergedArray = Array.from(mergedMap.values());
    return mergedArray;
  };

  const mergedArray = mergeDuplicateKeys([
    ...get().constraintChangesUpdate,
    ...tempKeysChangedArr,
  ]);

  if (tempKeysChangedArr.length === 0) {
  } else {
    set({
      constraintsMainMenuRedDot: true,
    });
    setTimeout(() => {
      set({
        newConstraintsAdded: true,
      });
    }, 500);
  }
  set({
    constraintChangesUpdate: mergedArray,
  });
};

//** specific utils for activityPanelStore */
const cleanRisk = (riskData: any) => {
  //** TODO ( fix on BE ). clean risk ( comes in wrong format from BE) */
  let tempTargetVar = null;
  if (riskData.target_var !== null) {
    tempTargetVar = riskData.target_var;
  }
  riskData = {
    financial_plan: riskData.financial_plan,
    risk_profile: riskData.risk_profile,
  };
  if (tempTargetVar) {
    riskData.target_var = tempTargetVar;
  }
  return riskData;
};

export const getConstraintDiff = (obj1: any, obj2: any) => {
  if (!obj1 || !obj2) return [];
  const diffKeys = [];
  const keys1 = Object.keys(obj1);
  //** avoid check with null object */
  if (keys1 !== null) {
    for (const key of keys1) {
      //** avoid check with null object */
      if (key !== null) {
        const val1 = obj1[key];
        const val2 = obj2[key];
        if (val1 !== val2) {
          diffKeys.push({
            key: key,
          });
        }
      }
    }
  }

  return diffKeys;
};

export const mergeAssetData = (data: ConstraintType["data"]) => {
  return {
    ...data.asset,
    etf_max: data.instrument_type.etf_max,
    etf_min: data.instrument_type.etf_min,
    mutual_funds_max: data.instrument_type.mutual_funds_max,
    mutual_funds_min: data.instrument_type.mutual_funds_min,
    bonds_max: data.instrument_type.bonds_max,
    bonds_min: data.instrument_type.bonds_min,
    core_max: data.core_satellite.core_max,
    core_min: data.core_satellite.core_min,
    satellite_max: data.core_satellite.satellite_max,
    satellite_min: data.core_satellite.satellite_min,
  };
};

export const handleConstraintChange = (
  get: StoreApi<any>["getState"],
  set: StoreApi<any>["setState"],
  constraints: ConstraintState,
) => {
  //* we have already saved constraints, so things have changed */
  if (constraints.messageId !== get().savedConstraintsState.messageId) {
    detectConstraintChanges(
      constraints.messageId,
      get().savedConstraintsState,
      null,
      get,
      set,
      constraints,
    );
    set({ savedConstraintsState: constraints });
  }
};
export const applyInitialConstraints = (
  get: StoreApi<any>["getState"],
  set: StoreApi<any>["setState"],
  constraints: ConstraintState,
) => {
  let constraintsState = null;
  if (get().constraintsDefault) {
    if (get().constraintsDefault.default_constraints) {
      const constraintsStateStandard = get().constraintsState;
      const constraintsStateDefault =
        get().constraintsDefault.default_constraints;
      const constraintsStateDefaultMacro =
        get().constraintsDefault.default_macro_constraints;
      const financialPlanStore = get().financialPlanStore;

      constraintsState = injectDefaultConstraints(
        constraintsStateStandard,
        constraintsStateDefault,
        constraintsStateDefaultMacro,
        financialPlanStore,
      );
    }
  }

  if (!constraintsState) {
    constraintsState = get().constraintsState;
  }

  let productException = false;
  let microCatException = false;
  //** fix exception for products and microcategoria */
  if (!isEmptyObject(constraintsState.data.product)) {
    if (!isEmptyObject(constraintsState.data.product.product_weights)) {
      productException = true;
    }
  }
  if (!isEmptyObject(constraintsState.data.microcategoria_advice)) {
    if (
      !isEmptyObject(
        constraintsState.data.microcategoria_advice.microcategory_weights,
      )
    ) {
      microCatException = true;
    }
  }

  const tempConstraintChangeCompare = structuredClone(constraintsState);
  tempConstraintChangeCompare.data["esg"] = {};
  detectConstraintChanges(
    constraints.messageId,
    tempConstraintChangeCompare,
    {
      productException,
      microCatException,
    },
    get,
    set,
    constraints,
  );

  set({ savedConstraintsState: constraints });
};
